import { getSharedConfig } from "../../createSharedConfig"
import { getBuyRequestMeta, getSimpleForBuyRequest } from "@/js/helpers/buy-request"
import type { KnownMaterials } from "@rogue/rackbuilder"
import { COLOR_TO_MATERIAL_KEYS } from "@/js/pages/product/rackbuilder/constants"
import { RackOptions, FrontRackOptions } from "@/js/pages/product/rackbuilder/RackTypes"
import { findBundleProduct, getAccessoryQuantity } from "@/js/pages/product/rackbuilder/utils/product-lookup"
import { getCommonAccessorySelections, getFrontSelection, getJCupSelection, getDepthSelection, getPlateStorageSelection, getBandPegSelection } from "@/js/pages/product/rackbuilder/utils/selection-utils"
import { IRogue, BuyRequest } from "@/js/services/RogueTypes"

// Config for the Rhino Rack, Rhino FXT, and Rhino INDY
export function createRhinoConfig(rogue: IRogue, buyRequest: BuyRequest): RackOptions {
  const meta = getBuyRequestMeta(buyRequest, rogue)
  const rack = findBundleProduct(rogue, buyRequest, 'rack') || getSimpleForBuyRequest(buyRequest, rogue)
  const rhino = findBundleProduct(rogue, buyRequest, 'trainer')

  const sharedConfig = getSharedConfig(rogue, buyRequest)
  const color = sharedConfig.color as string
  const isRhinoHalfRack = rack?.attributes?.rack_size === '4'
  const addIndy       = rhino?.attributes.rig_type?.toString().toLowerCase().includes('indy')
  const isRhinoRackTrainer = rhino?.attributes.rig_type === 'Rhino Rack Trainer'
  const leftSide = rhino?.attributes.side === 'Left' || rhino?.attributes.side === 'Dual'
  const rightSide = rhino?.attributes.side === 'Right' || rhino?.attributes.side === 'Dual'

  const materials = sharedConfig.materials || {}
  const { trolleySelection, lowRowSelection, latBarSelection } = getCommonAccessorySelections(rogue, buyRequest, materials)

  // Rhino top crossmember uses the rack color (or material key if it's a named material)
  materials.topCrossmember = { main: { name: 'metal', material: 'mg', color } }
  if (COLOR_TO_MATERIAL_KEYS.includes(color)) {
    materials.topCrossmember = { main: { material: color as KnownMaterials } }
  }

  let backSelection: RackOptions['back'] = 'crossmember top 4'
  let frontSelection: FrontRackOptions = getFrontSelection(rogue, buyRequest, meta) || 'skinny'

  if (isRhinoHalfRack) {
    backSelection = ''
    frontSelection = 'crossmember top 4'
  } else if (sharedConfig.height === '100') {
    backSelection = 'nameplate' // all 100" racks get a nameplate
  }

  return {
    ...sharedConfig,
    back             : backSelection,
    front            : frontSelection,
    edition          : 'rhino trainer',
    color,
    materials,
    jcup             : getJCupSelection(rogue, buyRequest),
    depth            : getDepthSelection(rack, meta),
    sixPost          : rack?.attributes.rack_size?.toString().indexOf('6') !== -1,
    plateStorage     : getPlateStorageSelection(rogue, buyRequest),
    bandPegs         : isRhinoRackTrainer || !isRhinoHalfRack ? getBandPegSelection(rogue, buyRequest) : undefined,
    storagePosts     : false,
    ghostRack        : false,
    ftShrouds        : Boolean(rhino?.attributes.rig_or_wm && rhino.attributes.rig_or_wm !== 'No'),
    spotterArms      : getAccessoryQuantity(rogue, buyRequest, 'safety spotter'),
    frontStabilizer  : Boolean(findBundleProduct(rogue, buyRequest, 'half rack feet', true)),
    addIndy,
    leftSide,
    rightSide,
    isRhinoHalfRack,
    rhinoRackTrainer : isRhinoRackTrainer,
    trolleySelection,
    latBarSelection,
    lowRowSelection,
  }
}
